<template>
  <div class="mb-3">
    <div v-for="incident in incidents" :key="incident.id" class="incident-wrapper">
      <div v-if="!incident.id" id="loan-incident-new" class="incident">
        <b-alert variant="warning" show class="mb-0 incident-header not-collapsed">
          <h4 class="alert-heading">
            <b-icon icon="exclamation-triangle-fill" />
            {{ $t("components.incident.loan_incidents.declare_incident") }}
          </h4>

          <ol v-if="loan.loanable.type === 'car'">
            <li>
              <strong>{{ $t("components.incident.loan_incidents.steps.breathe") }}</strong> :
              {{ $t("components.incident.loan_incidents.steps.breathe_desc") }}
            </li>
            <li>
              <strong>{{ $t("components.incident.loan_incidents.steps.select_type") }}</strong
              >:<br />

              {{ $t("components.incident.loan_incidents.steps.immobilized") }}
              <em>{{ $t("incidents.fields.type.options.accident") | capitalize }}</em
              ><br />

              {{ $t("components.incident.loan_incidents.steps.minor_damage") }}
              <em>{{ $t("incidents.fields.type.options.small_incident") | capitalize }}</em>
            </li>
            <li>
              <strong>{{
                $t("components.incident.loan_incidents.steps.follow_instructions")
              }}</strong>
            </li>
            <li>
              <strong>{{ $t("components.incident.loan_incidents.steps.return") }}</strong>
            </li>
          </ol>

          <div class="incident-details">
            <validation-observer ref="observer" v-slot="{ passes }">
              <b-form :novalidate="true" class="form loan-actions-incident__form">
                <forms-validated-input
                  v-if="incidentTypes.length > 1"
                  v-model="newIncident.incident_type"
                  name="incident_type"
                  :rules="{ required: true }"
                  :label="$t('components.incident.loan_incidents.form.incident_type')"
                  type="select"
                  :options="incidentTypes"
                />

                <forms-validated-input
                  v-model="newIncident.comments_on_incident"
                  name="comments_on_incident"
                  :rules="{ required: true }"
                  :label="$t('components.incident.loan_incidents.form.comment')"
                  type="markdown"
                />

                <div class="loan-actions-incident__buttons text-center">
                  <icon-button
                    size="sm"
                    variant="success"
                    class="mr-2"
                    :onclick="async () => passes(() => createIncident(incident))"
                  >
                    {{ $t("forms.create") | capitalize }}
                  </icon-button>

                  <icon-button size="sm" role="cancel" @click="cancelAdding">
                    {{ $t("forms.cancel") | capitalize }}
                  </icon-button>
                </div>
              </b-form>
            </validation-observer>
          </div>
        </b-alert>
      </div>
      <div v-else class="incident">
        <b-alert
          class="mb-0 incident-header"
          show
          :variant="incident.is_blocking ? 'danger' : 'warning'"
        >
          <h4 class="alert-heading">
            <b-icon icon="exclamation-triangle-fill" />

            {{
              (incident.is_blocking
                ? $t("incidents.fields.blocking_type.options.blocking")
                : $t("incidents.fields.blocking_type.options.non_blocking")) | capitalize
            }}
          </h4>

          <div
            v-if="
              incident.reported_by_user &&
              incident.reported_by_user.id === userId &&
              (!incident.loan_id || incident.loan_id === loan.id)
            "
            class="mb-2"
          >
            <strong>{{ $t("components.incident.loan_incidents.instructions.title") }}</strong>
            <div v-if="incident.incident_type === 'accident'">
              <ol>
                <li>
                  {{ $t("components.incident.loan_incidents.instructions.accident.desjardins")
                  }}<br />
                  <strong>{{
                    $t("components.incident.loan_incidents.instructions.accident.phone")
                  }}</strong
                  ><br />
                  {{ $t("components.incident.loan_incidents.instructions.accident.phone_number")
                  }}<br />
                  {{ $t("components.incident.loan_incidents.instructions.accident.option") }}<br />
                  <strong>{{
                    $t("components.incident.loan_incidents.instructions.accident.contract")
                  }}</strong
                  ><br />
                  {{
                    $t("components.incident.loan_incidents.instructions.accident.contract_number")
                  }}
                </li>
                <li>
                  {{ $t("components.incident.loan_incidents.instructions.accident.notify_owner") }}
                </li>
                <li>
                  {{
                    $t("components.incident.loan_incidents.instructions.accident.desjardins_repair")
                  }}
                </li>
              </ol>
            </div>
            <div v-else-if="incident.incident_type === 'small_incident'">
              <ol>
                <li>
                  {{
                    $t(
                      "components.incident.loan_incidents.instructions.small_incident.notify_owner"
                    )
                  }}
                </li>
                <li>
                  {{
                    $t(
                      "components.incident.loan_incidents.instructions.small_incident.owner_desjardins"
                    )
                  }}
                </li>
                <li>
                  {{
                    $t(
                      "components.incident.loan_incidents.instructions.small_incident.locomotion_info"
                    )
                  }}
                </li>
                <li>
                  {{
                    $t(
                      "components.incident.loan_incidents.instructions.small_incident.desjardins_repair"
                    )
                  }}
                </li>
              </ol>
            </div>

            <p v-else>
              {{ $t("components.incident.loan_incidents.instructions.other") }}
            </p>
          </div>
          <div v-else-if="!loan.id || loan.id === 'new'" class="mb-2">
            <template v-if="userIsBorrower && incident.is_blocking">
              <span v-if="dayjs(incident.blocking_until).isAfter(loan.departure_at, 'minute')">
                {{
                  $t("components.incident.loan_incidents.blocking_message.future", {
                    date: datetime(incident.blocking_until),
                  })
                }}
              </span>
              <span v-else>
                {{
                  $t("components.incident.loan_incidents.blocking_message.past", {
                    date: datetime(incident.blocking_until),
                  })
                }}
              </span>
            </template>
          </div>
          <div v-else class="mb-2">
            {{
              $t("components.incident.loan_incidents.blocking_message.affecting", {
                id: incident.id,
              })
            }}
            <span v-if="loanStartsInFuture && userIsBorrower && incident.is_blocking">
              {{
                $t("components.incident.loan_incidents.blocking_message.future_blocking", {
                  date: datetime(incident.blocking_until),
                })
              }}
            </span>
            <span v-else-if="!loanStartsInFuture">
              {{ $t("components.incident.loan_incidents.blocking_message.past_loan") }}
            </span>
          </div>

          <template v-if="!incident.details_hidden">
            <toggle-link class="d-block mt-1 mb-2" :collapse-id="`incident-details-${incident.id}`">
              {{ $t("components.incident.loan_incidents.details") }}
            </toggle-link>

            <b-collapse :id="`incident-details-${incident.id}`">
              <incident-form
                class="incident-details"
                :incident="incident"
                :loanable="loan.loanable"
                @update="updateIncident"
              />
            </b-collapse>
          </template>
        </b-alert>
      </div>
    </div>
  </div>
</template>

<script>
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import IncidentForm from "@/components/Incident/IncidentForm.vue";
import IconButton from "@/components/shared/IconButton.vue";
import ToggleLink from "@/components/shared/ToggleLink.vue";
import { datetime } from "@/helpers/filters";
import { isBorrower } from "@/helpers/permissions/loans";

import { post } from "@/requests/server";
import dayjs from "dayjs";

export default {
  name: "LoanIncidents",
  components: {
    ToggleLink,
    IncidentForm,
    IconButton,
    FormsValidatedInput,
  },
  props: {
    loan: {
      type: Object,
      required: true,
    },
    incidents: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      newIncident: {
        incident_type: null,
        comments_on_incident: null,
      },
    };
  },
  computed: {
    dayjs() {
      return dayjs;
    },
    incidentTypes() {
      switch (this.loan.loanable.type) {
        case "car":
          return [
            { value: "accident", text: "Véhicule immobilisé" },
            { value: "small_incident", text: "Dégâts mineurs" },
          ];
        default:
          return [{ value: "general", text: "Incident avec une remorque ou un vélo" }];
      }
    },
    loanStartsInFuture() {
      return this.$second.isBefore(this.loan.departure_at, "minute");
    },
    userIsBorrower() {
      return isBorrower(this.$store.state.user, this.loan);
    },
    userId() {
      return this.$store.state.user.id;
    },
  },
  methods: {
    datetime,
    updateIncident(incident) {
      if (incident.status === "completed") {
        // If updated incident has completed, simply remove it
        this.loan.loanable.active_incidents = this.loan.loanable.active_incidents.filter(
          (i) => i.id !== incident.id
        );
        return;
      }

      this.loan.loanable.active_incidents = this.loan.loanable.active_incidents.map((i) => {
        if (i.id === incident.id) {
          return incident;
        }
        return i;
      });
    },
    cancelAdding() {
      this.loan.loanable.active_incidents = this.loan.loanable.active_incidents.filter(
        (i) => !!i.id
      );
    },
    async createIncident() {
      if (this.loan.loanable.type !== "car") {
        this.newIncident.incident_type = "general";
      }
      const { data } = await post(
        `/incidents`,
        {
          type: "incident",
          loan_id: this.loan.id,
          loanable_id: this.loan.loanable.id,
          ...this.newIncident,
        },
        {
          notifications: {
            action: this.$t("components.incident.loan_incidents.notifications.create.action"),
          },
        }
      );
      this.loan.loanable.active_incidents = this.loan.loanable.active_incidents.filter(
        (i) => !!i.id
      );
      this.loan.loanable.active_incidents.push(data);
      this.$emit("created");
    },
  },
};
</script>

<style lang="scss">
.incident-wrapper + * {
  margin-top: 1rem;
}

.cursor-pointer {
  cursor: pointer;
}

.incident-header.alert {
  padding-bottom: 0.25rem;
}
.incident-details {
  border-radius: 0.5rem;
  background: $white;
  box-shadow: $small-shadow;
  color: $dark;
  padding: 1rem;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  textarea {
    font-size: 0.9rem;
  }
}
</style>
